import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: string;
  roles?: string[];
  tenantId?: string;
  avatarUrl?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** `rememberMe` (default true) picks which storage the session persists to: localStorage
   * survives browser restarts, sessionStorage clears when the tab/browser closes. Only
   * meaningful for the email/password login form -- OTP/social logins always remember. */
  login: (user: User, token: string, refreshToken: string, rememberMe?: boolean) => void;
  logout: () => void;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  setLoading: (loading: boolean) => void;
  refreshAuth: (token: string, refreshToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: (user, token, refreshToken, rememberMe = true) => {
        useAuthStore.persist.setOptions({
          storage: createJSONStorage(() => (rememberMe ? localStorage : sessionStorage)),
        });
        set({ user, token, refreshToken, isAuthenticated: true, isLoading: false });
      },

      logout: () =>
        set({ user: null, token: null, refreshToken: null, isAuthenticated: false }),

      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      setLoading: (isLoading) => set({ isLoading }),
      refreshAuth: (token, refreshToken) => set({ token, refreshToken }),
    }),
    {
      name: 'auth-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
